// W47b (2026-10-21): the rep's end-of-day letters queue on a real session,
// rendered from the W47 `selection` of GET /v1/letters/queue in the
// approved 19 layout (launch-ready/approved-stack-2026-10-12/
// 19-Letters-Consistency-Review-v5.html, READ: cc-hero, identity row,
// customer nav, task dock, sf-grid with the conversation card and the
// right stack, Up next, footer).
//
// What is real and what is not:
//   - every name, address, order fact, why-line and message-pack variant
//     comes from the HubSpot-selected item (letters-queue-source.mjs);
//   - "Letter Ready & Next", "Still needs attention" and "Mailed" are
//     PRESENT and INERT behind the live-write gate: nothing on this screen
//     writes, and the copy says so (feedback_end_of_day_letter_writing keeps
//     readiness and mailing as distinct events; W37b decision);
//   - the readiness receipts (`items`, Postgres letters.letter_readiness)
//     stay a separate "Ready for Shipping" history section, and "Mailed"
//     reads "Mailed dates are not tracked yet" while the api-server reports
//     NO_MAILING_EVENT_SOURCE;
//   - no fixture letter (Sarah, Taylor Brooks, ...) can reach this path: it
//     only mounts on a real session with a W47 body.
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MockCustomer } from '../../../data/mockData.js';
import { CustomerTabs } from '../../_shared/CustomerTabs.js';
import { Icon } from '../../_shared/IconSprite.js';
import { IdentityRow } from '../../_shared/IdentityRow.js';
import { RepriorityPill } from '../../_shared/PriorityFocus.js';
import { useReviewIdentity } from '../../_shared/useReviewIdentity.js';
import { LIVE_WRITE_PAUSED_LABEL } from '../../_w2a_lower/liveWrite.js';
import {
  ADDRESS_FIELD_LABELS,
  NO_LAPSED_CONTACTS_OWNED,
  NO_MAILING_EVENT_SOURCE,
  READINESS_RECORDED_TODAY,
  REP_HAS_NO_OWNER_RECORD,
  type LetterQueueRow,
  type LetterSelection,
  type LetterSelectionItem,
  type LettersMailed,
  type LettersQueueBody,
} from '../model.js';

export const LETTERS_NO_LAPSED_TITLE = 'No lapsed customers assigned to you yet';
export const LETTERS_MAILED_NOT_TRACKED = 'Mailed dates are not tracked yet';
export const LETTERS_PREFERENCE_NOTE = 'The customer’s request guides this conversation.';
const LETTERS_RETURN_STATE = { state: { returnTo: '/letters' } } as const;

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function lettersWord(n: number): string {
  return n === 1 ? 'letter' : 'letters';
}

function recordedLabel(ms: number | null): string {
  if (ms === null) return 'Recorded time unknown';
  return new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Honest title + body for a selection that carries no letter. */
export function emptySelectionCopy(selection: LetterSelection): { title: string; body: string } {
  const lapse = selection.rule?.lapseDays;
  if (!selection.available) {
    return {
      title: 'The letters selection is not connected on this deployment',
      body: `The api-server reported ${selection.reason ?? 'no HubSpot read'} for the end-of-day selection. Readiness receipts below are unaffected.`,
    };
  }
  if (selection.suppressed === READINESS_RECORDED_TODAY) {
    return {
      title: 'Tonight’s letters are recorded',
      body: selection.note ?? 'You already recorded letter readiness today; the selection re-opens tomorrow.',
    };
  }
  if (selection.reason === REP_HAS_NO_OWNER_RECORD) {
    return {
      title: 'No HubSpot owner record matches your session',
      body: selection.note ?? 'The queue cannot be scoped to a rep without a HubSpot owner record for this user id or email.',
    };
  }
  if (selection.reason === NO_LAPSED_CONTACTS_OWNED || selection.items.length === 0) {
    return {
      title: LETTERS_NO_LAPSED_TITLE,
      body: `HubSpot returned no customer owned by you with an order more than ${lapse ?? 'the lapse window of'} days ago and a mailing address on file. Contact owners are assigned by the re-import, so this is expected for every rep until it runs.`,
    };
  }
  return { title: 'No letters in tonight’s queue', body: 'HubSpot returned no lapsed customers for you under the letters rule.' };
}

function customerFromItem(item: LetterSelectionItem): MockCustomer {
  return {
    id: item.contactId,
    initials: item.initials,
    firstName: item.name.first ?? '',
    fullName: item.name.full,
    customerSince: 'date unknown',
    city: item.address.city ?? '',
    stateCode: item.address.state ?? '',
    localTime: '',
    phone: '',
    email: '',
    preferredChannel: '',
  };
}

function Hero({ remaining, title, body }: { remaining: number; title: string; body: string }): ReactNode {
  return (
    <section className="cc-hero" aria-label="Current task">
      <div className="sf-hero-channel">
        <Icon id="mail" />
        <div>
          <h1 id="sf-hero-task-label">Letter Follow-up</h1>
          <span id="sf-hero-task-context" data-testid="letters-remaining">End of day · {remaining} {lettersWord(remaining)} remaining</span>
        </div>
      </div>
      <div className="sf-hero-task-copy">
        <h2 id="sf-purpose">{title}</h2>
        <p id="sf-reason">{body}</p>
      </div>
      <div className="shared-repriority-heading">
        <RepriorityPill className="shared-repriority" />
      </div>
    </section>
  );
}

function AddressChecklist({ item }: { item: LetterSelectionItem }): ReactNode {
  const { complete, missing } = item.addressChecklist;
  return (
    <>
      <ul className="lt-address-checklist" aria-label="Address checklist" data-testid="letters-address-checklist" data-complete={complete ? 'true' : 'false'}>
        {ADDRESS_FIELD_LABELS.map(field => {
          const ok = !missing.includes(field.label);
          return <li key={field.key} data-ok={ok ? 'true' : 'false'}><span aria-hidden="true">{ok ? '✓' : '✗'}</span> {field.label}</li>;
        })}
      </ul>
      <p className="lt-address-missing" data-testid="letters-address-status">{complete ? 'Address complete for mailing.' : `Missing: ${missing.join(', ')}`}</p>
    </>
  );
}

function LastOrder({ item }: { item: LetterSelectionItem }): ReactNode {
  const o = item.lastOrder;
  return (
    <section className="card">
      <div className="context" data-testid="letters-last-order">
        <div className="eyebrow">LAST ORDER</div>
        <dl>
          <dt>Order</dt><dd>{o.name ?? 'Not on file'}</dd>
          <dt>Date</dt><dd>{o.date ? `${o.date}${o.daysSince !== null ? ` (${o.daysSince} days ago)` : ''}` : 'Not on file'}</dd>
          <dt>Orders</dt><dd>{o.ordersCount !== null ? String(o.ordersCount) : 'Not on file'}</dd>
          <dt>Total spent</dt><dd>{o.totalSpent !== null ? money.format(o.totalSpent) : 'Not on file'}</dd>
          {o.product ? <><dt>Product</dt><dd>{o.product}</dd></> : null}
        </dl>
      </div>
    </section>
  );
}

function MessagePack({ item }: { item: LetterSelectionItem }): ReactNode {
  const pack = item.messagePack;
  const docLabel = pack.doc ? pack.doc.split('/').pop() ?? pack.doc : 'the reactivation message pack';
  return (
    <div className="draft lt-message-pack" data-testid="letters-message-pack">
      <div className="draft-top">
        <strong>Message pack</strong>
        <span>{pack.variant ? `Variant ${pack.variant} · ${pack.label ?? ''}`.trim() : 'No variant selected'}</span>
      </div>
      <p className="lt-message-pack-body">
        {pack.variant
          ? `Use handwritten note template ${pack.variant} from ${docLabel}${pack.section ? `, section ${pack.section}` : ''}.`
          : pack.note ?? 'No message-pack variant applies to this customer.'}
      </p>
    </div>
  );
}

function LetterTask({ item, next, remaining }: { item: LetterSelectionItem; next: LetterSelectionItem | null; remaining: number }): ReactNode {
  const navigate = useNavigate();
  const identity = useReviewIdentity(item.contactId);
  // The profile read gives the honest phone / email / badge; until it is
  // ready (or if it fails) the identity row shows the HubSpot-selected
  // name and city only. The preview fixture can never reach this branch.
  const ready = !identity.isPreview && identity.status === 'ready';
  const customer = ready ? identity.customer : customerFromItem(item);
  const first = item.name.first ?? item.name.full;
  const a = item.address;
  const open = (path: string): void => navigate(`/${path}/${encodeURIComponent(item.contactId)}`, LETTERS_RETURN_STATE);
  return (
    <>
      <IdentityRow
        customer={customer}
        badgeLabel={ready ? identity.badgeLabel : 'Assigned letter'}
        sinceLabel={ready ? identity.sinceLabel : item.lastOrder.date ? `Last order ${item.lastOrder.date}` : ' '}
        preferenceNote={LETTERS_PREFERENCE_NOTE}
      />
      <div className="sf-customer-nav" id="sf-customer-nav">
        <div className="cc-file-shortcuts">
          <button type="button" onClick={() => open('customer-addresses')}>Customer &amp; Addresses</button>
          <button type="button" onClick={() => open('edit-preferences')}>Edit Preferences</button>
        </div>
        <CustomerTabs customerId={item.contactId} active="current" />
      </div>
      <section className="sf-task-dock" aria-label="Current letter">
        <div className="sf-task-identity">
          <div className="sf-task-person">
            <span className="sf-task-avatar" id="sf-task-avatar" aria-hidden="true">{item.initials}</span>
            <div>
              <h3 id="sf-dock-recipient">{item.name.full}</h3>
              <p id="sf-dock-destination">{[a.city, a.state].filter(Boolean).join(', ') || 'Destination not on file'}</p>
            </div>
          </div>
          <span className="sf-task-badge" id="sf-direction-label">Handwritten Letter</span>
          <div className="sf-task-dock-cue"><b id="sf-dock-cue-title">Letter &amp; envelope</b><span>Confirm when ready for shipping</span></div>
        </div>
      </section>
      <div id="sf-task-workspace">
        <div className="sf-grid">
          <section className="card conversation" aria-label="Current customer letter">
            <div className="card-head">
              <div className="row"><span className="head-icon"><Icon id="mail" /></span><h3 id="sf-message-heading">Letter to {first}</h3></div>
              <span className="sf-channel">Handwritten letter</span>
            </div>
            <div className="thread lt-purpose">
              <span className="eyebrow">WHY THIS LETTER</span>
              <p id="lt-reason" data-testid="letters-why">{item.why ?? 'No reason recorded on the selection.'}</p>
            </div>
            <MessagePack item={item} />
            <div className="lt-handoff">
              <Icon id="box" />
              <div><strong>Write the letter and prepare the envelope.</strong><p>Give completed letters to Shipping. They will mail them for you.</p></div>
            </div>
            <div className="sf-status pending" id="sf-result" role="status">
              <strong>Readiness is not recorded from this screen yet.</strong>
              <p>{LIVE_WRITE_PAUSED_LABEL}: Letter Ready &amp; Next, Still needs attention and Mailed stay inert until the write gate opens.</p>
            </div>
            <div className="sf-retain-row">
              <span>Missing information or unable to prepare this letter?</span>
              <button type="button" id="sf-retain" disabled aria-describedby="finish-reason" title={LIVE_WRITE_PAUSED_LABEL}>Still needs attention</button>
            </div>
          </section>
          <aside className="right-stack">
            <section className="card">
              <div className="card-head"><div className="row"><span className="head-icon"><Icon id="pin" /></span><h3>Mailing address</h3></div></div>
              <div className="context lt-address">
                <strong id="lt-address-recipient">{a.recipient ?? item.name.full}</strong>
                <p id="lt-address-lines" data-testid="letters-address-lines">
                  {a.line1 ?? 'Street not on file'}
                  {a.line2 ? <><br />{a.line2}</> : null}
                  <br />
                  {[a.city, a.state].filter(Boolean).join(', ')}{a.zip ? ` ${a.zip}` : ''}
                </p>
                <span className="lt-address-source">Mailing address from the HubSpot contact</span>
                <AddressChecklist item={item} />
              </div>
              <button type="button" className="card-link" onClick={() => open('customer-addresses')}>Customer &amp; Addresses<Icon id="arrow" /></button>
            </section>
            <section className="card sf-guidance">
              <span className="eyebrow">LETTER GUIDE</span>
              <h3>Make it personal.</h3>
              <p id="lt-guide">Ask how {first}{item.horse ? ` and ${item.horse}` : ''} are doing. Offer help without pushing another order.</p>
            </section>
            <section className="card">
              <div className="context">
                <div className="eyebrow">CURRENT TASK</div>
                <dl>
                  <dt>Purpose</dt><dd>Reactivation</dd>
                  <dt>Channel</dt><dd>Handwritten letter</dd>
                  <dt>Recipient</dt><dd id="sf-task-recipient">{item.name.full}</dd>
                  <dt>Status</dt><dd id="sf-task-status">Prepare letter &amp; envelope</dd>
                </dl>
              </div>
            </section>
            <LastOrder item={item} />
          </aside>
        </div>
      </div>
      <section className="next" aria-label="Next work preview" id="lt-next" data-testid="letters-up-next">
        <span className="eyebrow">UP NEXT</span>
        {next ? (
          <>
            <div className="avatar" id="lt-next-avatar">{next.initials}</div>
            <div><strong id="lt-next-name">{next.name.full}</strong><p>Handwritten letter</p></div>
            <div className="reason"><strong id="lt-next-count">Next letter</strong><p>{remaining - 1} more after this one</p></div>
            <button type="button" className="text-btn lt-next-open" onClick={() => navigate(`/letters/${encodeURIComponent(next.contactId)}`)}>Open next letter</button>
          </>
        ) : (
          <div><strong id="lt-next-name">Last letter tonight</strong><p>Hand completed letters and envelopes to Shipping.</p></div>
        )}
        <Icon id="arrow" />
      </section>
    </>
  );
}

function ReceiptsHistory({ receipts, mailed }: { receipts: LetterQueueRow[]; mailed: LettersMailed | null }): ReactNode {
  const navigate = useNavigate();
  const mailedCopy = mailed === null || mailed.reason === NO_MAILING_EVENT_SOURCE || !mailed.available
    ? LETTERS_MAILED_NOT_TRACKED
    : 'Mailing events are recorded by the api-server.';
  return (
    <section className="card lt-history" data-testid="letters-live-queue" aria-label="Readiness receipts">
      <div className="context">
        <div className="eyebrow">READINESS RECEIPTS</div>
        <h3>{receipts.length} {lettersWord(receipts.length)} ready for Shipping</h3>
        <p className="muted">Receipts recorded by the readiness workflow (letters.letter_readiness). Readiness and actual mailing are separate events.</p>
        <div className="lt-history-group" data-testid="letters-ready-group">
          <span className="eyebrow">READY FOR SHIPPING</span>
          {receipts.length === 0 ? (
            <p data-testid="letters-live-empty">No letter has been recorded as ready for Shipping yet.</p>
          ) : (
            <ul className="lt-receipts">
              {receipts.map((row, index) => (
                <li key={row.taskId ?? `${row.customerId ?? 'unknown'}-${index}`} data-testid="letters-live-item">
                  <strong>{row.customerId ? `HubSpot contact ${row.customerId}` : 'Customer unknown'}</strong> · {row.kind ?? 'letter'} · {recordedLabel(row.recordedAt)}{row.preparedBy ? ` · prepared by ${row.preparedBy}` : ''}
                  {row.note ? <p>{row.note}</p> : null}
                  {row.customerId ? <button className="text-btn" type="button" onClick={() => navigate(`/current-action/${encodeURIComponent(row.customerId!)}`)}>Open customer →</button> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="lt-history-group" data-testid="letters-mailed-group">
          <span className="eyebrow">MAILED</span>
          <p data-testid="letters-mailed-note">{mailedCopy}</p>
        </div>
      </div>
    </section>
  );
}

export interface EndOfDayLettersProps {
  body: LettersQueueBody;
  /** /letters/:customerId opens that letter when it is in tonight's selection. */
  customerId?: string;
}

export function EndOfDayLetters({ body, customerId }: EndOfDayLettersProps): ReactNode {
  const selection = body.selection;
  const items = selection?.items ?? [];
  const requested = customerId ? items.findIndex(item => item.contactId === customerId) : -1;
  const index = requested >= 0 ? requested : 0;
  const current = items[index] ?? null;
  const next = items[index + 1] ?? null;
  const remaining = Math.max(0, items.length - index);
  const empty = selection && !current ? emptySelectionCopy(selection) : null;
  return (
    <div className="sf-main cc-main" id="sf-main" data-testid="letters-end-of-day">
      <Hero
        remaining={remaining}
        title={current ? `Reconnect with ${current.name.first ?? current.name.full}` : 'No letters to write tonight'}
        body={current ? 'Write a personal letter, then prepare the envelope for shipping.' : 'The reason is below; the readiness receipts are unchanged.'}
      />
      {current ? (
        <LetterTask key={current.contactId} item={current} next={next} remaining={remaining} />
      ) : (
        <>
          <section className="card lt-complete" role="status" data-testid="letters-selection-empty">
            <Icon id="mail" />
            <h2 id="lt-complete-title">{empty?.title ?? 'No letters tonight'}</h2>
            <p id="lt-complete-copy">{empty?.body ?? ''}</p>
          </section>
          <section className="next" aria-label="Next work preview" id="lt-next" data-testid="letters-up-next">
            <span className="eyebrow">UP NEXT</span>
            <div><strong id="lt-next-name">No letters queued tonight</strong><p>The end-of-day selection re-runs each day from HubSpot.</p></div>
            <Icon id="arrow" />
          </section>
        </>
      )}
      <ReceiptsHistory receipts={body.receipts} mailed={body.mailed} />
      <footer className="footer" data-component="action-footer">
        <button id="complete" className="finish" data-action="finish-next" data-approved-finish="true" aria-describedby="finish-reason" type="button" disabled title={LIVE_WRITE_PAUSED_LABEL}>
          Letter Ready &amp; Next<Icon id="arrow" />
        </button>
        <button id="lt-mailed" className="sf-secondary" type="button" disabled aria-describedby="finish-reason" title={LIVE_WRITE_PAUSED_LABEL}>Mailed</button>
        <span id="finish-reason" className="footer-note" data-testid="letters-finish-reason">{LIVE_WRITE_PAUSED_LABEL}. Readiness and mailing are not recorded from this screen yet; Shipping mails the letters.</span>
        <div className="footer-summary">
          <strong>{remaining} {lettersWord(remaining)} remaining</strong>
          <p id="lt-remaining">Readiness and actual mailing are separate events.</p>
        </div>
      </footer>
    </div>
  );
}
